import * as React from 'react'
import DatePicker, { registerLocale } from 'react-datepicker-styled-components'
import { colors } from '../../../styleConstants'
import styled from 'styled-components'
import cc from 'classcat'
import ja from 'date-fns/locale/ja'
import en from 'date-fns/locale/en-US'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  readonly locale: string
  readonly placeholder: string
  readonly iconRight?: JSX.Element
  readonly fluid?: boolean
  readonly className?: string
  readonly style?: React.CSSProperties
  readonly ref?: React.RefObject<HTMLInputElement>
  readonly onEnterKeyDown?: React.KeyboardEventHandler
  readonly onChanged?: (date: any) => void
}

interface State {
  readonly iconRightWidth?: number
}

export default function DatePickerWrapper({ locale, placeholder, fluid, className, style, iconRight, onChanged }: Props) {
  const iconRightRef = React.useRef<Element>(null)
  const [state, setState] = React.useState<State>({})
  const [startDate, setStartDate] = React.useState(null)

  const handleChange = (date: any) => {
    setStartDate(date)
    if (onChanged) {
      onChanged(date)
    }
  }

  if (locale == 'ja') {
    registerLocale(locale, ja)
  } else {
    registerLocale(locale, en)
  }

  React.useEffect(() => {
    let nextState: State = {}
    if (iconRightRef.current) {
      nextState = { ...nextState, iconRightWidth: iconRightRef.current.getBoundingClientRect().width }
    }
    setState(nextState)
  }, [])

  const inputStyle = {
    paddingRight: state.iconRightWidth ? `calc(${state.iconRightWidth}px + 0.25rem + 4px)` : undefined,
  }

  return (
    <Container className={cc([{ fluid }, className])} style={style}>
      <label className={'date-picker-label'}>
        <LocaleDatePicker
          wrapperClassName="datePicker"
          locale={locale}
          placeholderText={placeholder}
          onChange={handleChange}
          dateFormat="yyyy/MM/dd"
          style={inputStyle}
          selected={startDate}
          showYearDropdown
          yearDropdownItemNumber={115}
          scrollableYearDropdown
          maxDate={new Date()}
          />
        {iconRight && React.cloneElement(iconRight, { 'data-icon': 'right', ref: iconRightRef })}
      </label>
    </Container>
  )
}

const Container = styled.div`
  display: inline-flex;
  color: ${colors.gray800};
  position: relative;
  
  &.fluid {
    display: flex;
  }

  [data-icon='right'] {
    position: absolute;
    top: 50%;
    right: 4px;
    transform: translateY(-50%);
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`

const LocaleDatePicker = styled(DatePicker)`
  width: 26.5em;
  flex: 1 0 auto;
  max-width: 100%;
  outline: 0;
  border-radius: 4px;
  box-shadow: none;
  color: ${colors.gray800};
  background: ${colors.white};
  border: 1px solid ${colors.gray300};
  padding: 0.5rem;

  &:active,
  &:focus {
    border: 1px solid ${colors.secondary};
  }

  &:empty:required {
    border: 1px solid ${colors.gray300};
  }

  &:invalid {
    border: 1px solid ${colors.error};
  }
`